"use client";

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar, User, Eye, ArrowRight, Newspaper, Clock, Sparkles } from "lucide-react";
import Link from "next/link";

interface NewsMedia {
  url: string;
  type: 'image' | 'video';
  position: number;
}

interface News {
  _id: string;
  title: string;
  content: string;
  media: NewsMedia[];
  author: {
    _id: string;
    name: string;
    email: string;
  };
  status: 'pending' | 'approved' | 'rejected';
  previewUrl?: string;
  createdAt: string;
  updatedAt: string;
  views?: number;
}

export default function NewsSection() {
  const [news, setNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [featuredNews, setFeaturedNews] = useState<News | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // Dimensões padronizadas para as imagens
  const IMAGE_SIZES = {
    featured: {
      width: 800,
      height: 500,
      mobile: {
        width: 600,
        height: 400
      }
    },
    grid: {
      width: 400,
      height: 250,
      mobile: {
        width: 300,
        height: 200
      }
    }
  };

  useEffect(() => {
    fetchApprovedNews();
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth < 768);
  };

  const fetchApprovedNews = async () => {
    try {
      const response = await fetch('/api/news/approved');
      
      if (response.ok) {
        const data = await response.json();
        setNews(data);
        
        // Define the last approved news as featured
        if (data.length > 0) {
          setFeaturedNews(data[data.length - 1]);
        }
      } else {
        setError('Error loading news');
      }
    } catch (err) {
      setError('Connection error');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Função para criar URL otimizada da imagem
  const getOptimizedImageUrl = (originalUrl: string, type: 'featured' | 'grid') => {
    if (!originalUrl) return '/placeholder-news.jpg';
    
    // Se a imagem já vem de um serviço de otimização (como Cloudinary, ImageKit, etc.)
    // você pode adicionar parâmetros de transformação aqui
    const sizes = isMobile ? IMAGE_SIZES[type].mobile : IMAGE_SIZES[type];
    
    // Exemplo para Cloudinary (ajuste conforme seu provedor)
    if (originalUrl.includes('cloudinary')) {
      return originalUrl.replace('/upload/', `/upload/w_${sizes.width},h_${sizes.height},c_fill/`);
    }
    
    // Exemplo para ImageKit
    if (originalUrl.includes('ik.imagekit.io')) {
      return `${originalUrl}?tr=w-${sizes.width},h-${sizes.height},c-maintain_ratio`;
    }
    
    // Para imagens locais ou sem otimização, retornar a original
    // Em produção, considere usar o componente next/image para otimização
    return originalUrl;
  };

  // Componente de imagem padronizado
  const StandardizedImage = ({ 
    src, 
    alt, 
    type = 'grid',
    className = "",
    ...props 
  }: {
    src: string;
    alt: string;
    type: 'featured' | 'grid';
    className?: string;
    [key: string]: any;
  }) => {
    const [imageError, setImageError] = useState(false);
    const optimizedSrc = getOptimizedImageUrl(src, type);
    const sizes = isMobile ? IMAGE_SIZES[type].mobile : IMAGE_SIZES[type];

    const handleError = () => {
      setImageError(true);
    };

    if (imageError) {
      return (
        <div 
          className={`bg-gray-800 flex items-center justify-center ${className}`}
          style={{
            width: '100%',
            height: `${sizes.height}px`,
            minHeight: `${sizes.height}px`
          }}
        >
          <Newspaper className="w-12 h-12 text-gray-500" />
        </div>
      );
    }

    return (
      <div className="relative overflow-hidden" style={{ height: `${sizes.height}px` }}>
        <img
          src={optimizedSrc}
          alt={alt}
          className={`w-full h-full object-cover transition-transform duration-500 ${className}`}
          onError={handleError}
          loading="lazy"
          {...props}
        />
      </div>
    );
  };

  // Function to extract the first paragraph from content
  const getFirstParagraph = (content: string, maxLength: number = 200) => {
    const cleanContent = content.replace(/\[(IMAGE|VIDEO|LINK):[^\]]+\]/g, '').trim();
    const paragraphs = cleanContent.split('\n').filter(p => p.trim().length > 0);
    
    if (paragraphs.length > 0) {
      const firstParagraph = paragraphs[0].trim();
      if (firstParagraph.length > maxLength) {
        return firstParagraph.substring(0, maxLength) + '...';
      }
      return firstParagraph;
    }
    
    return 'Check the full article for more details.';
  };

  // Function to get relative time
  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours === 1) return '1h ago';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays === 1) return '1d ago';
    return `${diffInDays}d ago`;
  };

  // Format date for mobile
  const formatDateMobile = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { 
      y: 20, 
      opacity: 0,
      scale: 0.95
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut" as const
      }
    }
  };

  const headerVariants = {
    hidden: { 
      y: -30, 
      opacity: 0,
      scale: 0.9
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut" as const
      }
    }
  };

  // Get the news items to display in the grid
  const getGridNews = () => {
    const newsWithoutFeatured = news.filter(item => item._id !== featuredNews?._id);
    return isMobile ? newsWithoutFeatured.slice(0, 4) : newsWithoutFeatured.slice(0, 8);
  };

  if (loading) {
    return (
      <section className="relative w-full min-h-screen bg-black text-white py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto mb-4"></div>
            <div className="text-lg md:text-xl">Loading latest news...</div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="relative w-full min-h-screen bg-black text-white py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <div className="text-red-400 text-lg md:text-xl mb-4">{error}</div>
          <button 
            onClick={fetchApprovedNews}
            className="px-6 py-2 bg-yellow-500 text-black rounded-lg font-medium hover:bg-yellow-400 transition-colors"
          >
            Try Again
          </button>
        </div>
      </section>
    );
  }

  if (news.length === 0) {
    return (
      <section className="relative w-full min-h-screen bg-black text-white py-16 md:py-20">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <Newspaper className="w-16 h-16 md:w-20 md:h-20 text-gray-400 mx-auto mb-4" />
          <div className="text-xl md:text-2xl text-gray-300 mb-3">No news published yet</div>
          <p className="text-gray-400 text-lg">Approved news will appear here soon.</p>
        </div>
      </section>
    );
  }

  const gridNews = getGridNews();

  return (
    <section className="relative w-full min-h-screen bg-black text-white overflow-hidden">
      {/* Enhanced Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900 z-0" />
      
      {/* Dynamic Light Effects */}
      <div className="absolute inset-0 opacity-[0.03] md:opacity-5">
        <div className="absolute top-10 left-4 md:top-20 md:left-10 w-48 h-48 md:w-72 md:h-72 bg-blue-500 rounded-full blur-2xl md:blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-4 md:bottom-20 md:right-10 w-64 h-64 md:w-96 md:h-96 bg-purple-500 rounded-full blur-2xl md:blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 md:w-64 md:h-64 bg-yellow-500 rounded-full blur-2xl md:blur-3xl animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 container mx-auto px-4 sm:px-6 py-12 md:py-20">
        {/* Enhanced Header */}
        <motion.div 
          className="text-center mb-12 md:mb-16"
          variants={headerVariants}
          initial="hidden"
          animate="visible"
        >
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-4 md:mb-6 bg-gradient-to-r from-white via-yellow-200 to-yellow-400 bg-clip-text text-transparent leading-tight">
            Breaking <span className="text-yellow-500">News</span>
          </h2>
          
          <p className="text-lg md:text-xl lg:text-2xl text-gray-300 max-w-2xl md:max-w-3xl mx-auto leading-relaxed px-4">
            Stay informed with real-time updates and exclusive stories
          </p>
        </motion.div>

        {/* Enhanced Featured News */}
        {featuredNews && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-12 md:mb-16"
          >
            <div className="group relative bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-2xl md:rounded-3xl border border-gray-800 overflow-hidden shadow-2xl hover:shadow-2xl hover:shadow-yellow-500/20 transition-all duration-500">
              {/* Featured Badge */}
              <div className="absolute top-4 left-4 md:top-6 md:left-6 z-20">
                <span className="bg-yellow-500 text-black px-3 py-1.5 md:px-3 md:py-1 rounded-full text-xs md:text-sm font-bold uppercase tracking-wide z-20 flex items-center">
                  <Sparkles className="w-3 h-3 md:w-4 md:h-4 mr-1" />
                  Featured
                </span>
              </div>
              
              <div className="lg:flex">
                {/* Main Image - AGORA PADRONIZADA */}
                {featuredNews.media.find((m: NewsMedia) => m.type === 'image') && (
                  <div className="lg:flex-shrink-0 lg:w-1/2 relative overflow-hidden">
                    <StandardizedImage
                      src={featuredNews.media.find((m: NewsMedia) => m.type === 'image')!.url}
                      alt={featuredNews.title}
                      type="featured"
                      className="transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 h-24 md:h-32 bg-gradient-to-t from-black to-transparent" />
                  </div>
                )}
                
                {/* Main Content */}
                <div className="p-6 md:p-8 lg:p-10 flex-1 relative">
                  {/* Enhanced Metadata */}
                  <div className="flex flex-wrap items-center gap-3 md:gap-4 mb-4 text-sm text-gray-300">
                    <div className="flex items-center bg-gray-800/50 px-3 py-1.5 rounded-full">
                      <Clock className="w-3 h-3 md:w-4 md:h-4 mr-2 text-yellow-500" />
                      <span className="text-xs md:text-sm">{getTimeAgo(featuredNews.createdAt)}</span>
                    </div>
                    <div className="flex items-center">
                      <User className="w-3 h-3 md:w-4 md:h-4 mr-2 text-yellow-500" />
                      <span className="text-xs md:text-sm font-medium">{featuredNews.author.name}</span>
                    </div>
                    <div className="flex items-center">
                      <Eye className="w-3 h-3 md:w-4 md:h-4 mr-2 text-yellow-500" />
                      <span className="text-xs md:text-sm">{featuredNews.views || 0}</span>
                    </div>
                  </div>
                  
                  {/* Enhanced Title */}
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6 leading-tight line-clamp-3">
                    {featuredNews.title}
                  </h3>
                  
                  {/* Enhanced First Paragraph */}
                  <div className="mb-6 md:mb-8">
                    <p className="text-base md:text-lg text-gray-200 leading-7 md:leading-8 line-clamp-3 md:line-clamp-4">
                      {getFirstParagraph(featuredNews.content, isMobile ? 120 : 200)}
                    </p>
                  </div>
                  
                  {/* Enhanced Action Button */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <Link
                      href={`/new/${featuredNews._id}`}
                      className="group/btn inline-flex items-center justify-center px-6 py-3 bg-yellow-500 text-black rounded-xl font-bold hover:bg-yellow-400 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-yellow-500/30 text-sm md:text-base"
                    >
                      Read Full Story
                      <ArrowRight className="w-4 h-4 md:w-5 md:h-5 ml-2 md:ml-3 group-hover/btn:translate-x-1 transition-transform" />
                    </Link>
                    
                    <div className="text-xs md:text-sm text-gray-400 text-center sm:text-right">
                      {formatDateMobile(featuredNews.createdAt)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Enhanced Secondary News Grid */}
        {gridNews.length > 0 && (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 lg:gap-8 mb-12 md:mb-16"
          >
            {gridNews.map((newsItem, index) => (
              <motion.div
                key={newsItem._id}
                variants={itemVariants}
                whileHover={{ 
                  y: isMobile ? -4 : -8,
                  scale: isMobile ? 1.01 : 1.02,
                  transition: { duration: 0.3 }
                }}
                className="group"
              >
                <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl md:rounded-2xl border border-gray-800 overflow-hidden shadow-lg hover:shadow-xl hover:shadow-yellow-500/10 transition-all duration-300 h-full flex flex-col">
                  {/* Enhanced Image - AGORA PADRONIZADA */}
                  {newsItem.media.find((m: NewsMedia) => m.type === 'image') && (
                    <div className="relative overflow-hidden">
                      <StandardizedImage
                        src={newsItem.media.find((m: NewsMedia) => m.type === 'image')!.url}
                        alt={newsItem.title}
                        type="grid"
                        className="transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute top-3 left-3">
                        <span className="bg-black/70 text-white px-2 py-1 rounded text-xs font-medium">
                          {getTimeAgo(newsItem.createdAt)}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* Enhanced Content */}
                  <div className="p-4 md:p-6 flex-1 flex flex-col">
                    {/* Enhanced Metadata */}
                    <div className="flex items-center text-xs text-gray-400 mb-2 md:mb-3 gap-2">
                      <User className="w-3 h-3" />
                      <span className="truncate">{newsItem.author.name}</span>
                    </div>
                    
                    {/* Enhanced Title */}
                    <h4 className="text-base md:text-lg font-bold text-white mb-2 md:mb-3 line-clamp-2 leading-tight group-hover:text-yellow-400 transition-colors min-h-[3rem]">
                      {newsItem.title}
                    </h4>
                    
                    {/* Enhanced First Paragraph */}
                    <p className="text-gray-300 text-xs md:text-sm mb-3 md:mb-4 line-clamp-2 md:line-clamp-3 flex-1 leading-relaxed">
                      {getFirstParagraph(newsItem.content, isMobile ? 80 : 120)}...
                    </p>
                    
                    {/* Enhanced Footer */}
                    <div className="flex items-center justify-between mt-auto pt-3 md:pt-4 border-t border-gray-800">
                      <Link
                        href={`/new/${newsItem._id}`}
                        className="inline-flex items-center text-yellow-500 hover:text-yellow-400 font-medium transition-colors text-xs md:text-sm group/link"
                      >
                        Read more
                        <ArrowRight className="w-3 h-3 md:w-4 md:h-4 ml-1 md:ml-2 group-hover/link:translate-x-1 transition-transform" />
                      </Link>
                      
                      <div className="flex items-center text-xs text-gray-500">
                        <Eye className="w-3 h-3 mr-1" />
                        <span>{newsItem.views || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Enhanced See All News Button */}
        {news.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="text-center"
          >
            <Link
              href="/all-news"
              className="inline-flex items-center px-6 py-3 md:px-8 md:py-4 border-2 border-yellow-500 text-yellow-500 rounded-xl md:rounded-2xl font-bold hover:bg-yellow-500 hover:text-black transition-all duration-300 transform hover:scale-105 text-sm md:text-base"
            >
              <Newspaper className="w-4 h-4 md:w-5 md:h-5 mr-2 md:mr-3" />
              View All News 
            </Link>
          </motion.div>
        )}
      </div>

      {/* Enhanced Footer Glow */}
      <div className="absolute bottom-0 left-0 right-0 h-32 md:h-48 bg-gradient-to-t from-black to-transparent z-0" />
    </section>
  );
}